"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const _ = require("lodash");
const subscription_builder_1 = require("./subscription-builder");
const utils_1 = require("../utils");
/**
 * Interfaces with Notify on a given topic.
 *
 * @class TopicFocusedNotify
 * @param T a union of valid resource types
 */
class TopicFocusedNotify {
    /**
     * Creates an instance of the class.
     *
     * @param params.notify the instance of Notify used for operations
     * @param params.topic the topic with which we are concerned
     */
    constructor(params) {
        this.subscriptionMap = new Map();
        this.refCountMap = new Map();
        this.notify = params.notify;
        this.topic = params.topic;
        this.clearKnownSubscriptions();
    }
    /**
     * Returns an observable for add/edit events for the subscription.
     *
     * @param {Subscription | string} params.subscription subscription to match.
     * @param {T} params.resource resource to match.
     */
    asChangedObservable(params) {
        return this.asObservable(params)
            .filter(publication => publication.method !== 'DELETE')
            .map(publication => publication.content);
    }
    /**
     * Returns an observable for delete events for the subscription or resource.
     *
     * @param {Subscription | string} params.subscription subscription to match.
     * @param {T} params.resource resource to match.
     */
    asDeletedObservable(params) {
        return this.asObservable(params)
            .filter(publication => publication.method === 'DELETE')
            .map(publication => _.last(publication.subject.split('/')));
    }
    /**
     * Returns a list of the subscriptions tracked by this instance.
     */
    getSubscriptions(_params = {}) {
        return Array.from(this.subscriptionMap.entries()).map(pair => pair[1]);
    }
    /**
     * Drops tracked memory subscriptions.  Should only be called if you really know what you're doing.
     */
    clearKnownSubscriptions(_params = {}) {
        this.subscriptionMap.clear();
        this.refCountMap.clear();
    }
    /**
     * Creates a subscription builder for multi-resource subscriptions.
     */
    beginSubscribe(_params = {}) {
        return new subscription_builder_1.SubscriptionBuilder({ complete: this.createSubscription.bind(this) });
    }
    /**
     * Creates a single resource subscription.
     *
     * @param {T} params.type target resource type
     * @param {string} params.target optional specific resource target
     * @param {boolean} params.escapeTarget optional escape regex characters in the provided target
     */
    subscribe(params) {
        return this.beginSubscribe()
            .appendResource(params)
            .endSubscribe(params);
    }
    /**
     * Unsubscribe from events for the given params.  No params implies remove all tracked subscriptions and implies force.  Other params are ref counted so the server side subscription will only be removed when the ref count hits 0 unless the force param is set.
     *
     * @param {Subscription | Subscription[]} params.subscriptions subscriptions from which to unsubscribe
     * @param {Subscription | Subscription[]} params.subscriptionIds subscriptions from which to unsubscribe
     * @param {Transport} params.transport transport to unsubscribe
     * @param {boolean} params.force ignore ref counts and remove the subscription
     */
    unsubscribe(params = {}) {
        const subscriptionIds = [];
        if (!params.transport && !params.subscriptions && !params.subscriptionIds) {
            params.force = true;
            subscriptionIds.push(...this.getSubscriptionIds());
        }
        else {
            const givenIds = utils_1.Utils.toArray(params.subscriptionIds)
                .concat(this.toSubscriptionIds(utils_1.Utils.toArray(params.subscriptions)));
            subscriptionIds.push(...givenIds.filter(subscriptionId => this.subscriptionMap.has(subscriptionId)));
            if (params.transport) {
                subscriptionIds.push(...this.getSubscriptionIdsFor({ transport: params.transport }));
            }
        }
        return Promise.all(_.uniq(subscriptionIds)
            .map(subscriptionId => this.removeRef(subscriptionId, params.force)))
            .then(subscriptions => subscriptions.filter(subscription => subscription.subscriptionId != null));
    }
    /**
     * Removes a reference to a subscription.  Returns the subscription if unsubscribed and an empty object if not.
     *
     * @param subscriptionId
     * @param force
     */
    removeRef(subscriptionId, force) {
        const refs = this.decrementRef(subscriptionId);
        if (force || refs === 0) {
            this.subscriptionMap.delete(subscriptionId);
            return this.notify.unsubscribe({ subscriptionId })
                .catch(response => {
                if (response.statusCode === 404) {
                    return { subscriptionId };
                }
                else {
                    throw response;
                }
            });
        }
        else {
            return Promise.resolve({});
        }
    }
    createSubscription(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let subscription = this.getSubscriptionFor(params);
                if (subscription) {
                    if (params.expiryDateTime && params.expiryDateTime != subscription.expiryDateTime) {
                        try {
                            subscription = yield this.notify.updateSubscription({
                                subscriptionId: subscription.subscriptionId,
                                body: { expiryDateTime: params.expiryDateTime }
                            });
                        }
                        catch (error) {
                            if (error.statusCode === 404) {
                                yield this.removeRef(subscription.subscriptionId, true);
                                return yield this.createSubscription(params);
                            }
                            else {
                                throw error;
                            }
                        }
                    }
                }
                else {
                    subscription = yield this.notify.subscribe(Object.assign(Object.assign({}, params), { topic: this.topic }));
                }
                this.subscriptionMap.set(subscription.subscriptionId, subscription);
                this.incrementRef(subscription.subscriptionId);
                return subscription;
            }
            catch (error) {
                const errorString = error instanceof Error ? error.message : JSON.stringify(error);
                throw new Error(`Subscription Failed [${params.transport}:${params.subjectFilter}]. Error: [${errorString}]`);
            }
        });
    }
    incrementRef(subscriptionId) {
        const refCount = 1 + this.getRefCount(subscriptionId);
        this.refCountMap.set(subscriptionId, refCount);
        return refCount;
    }
    getRefCount(subscriptionId) {
        return this.refCountMap.get(subscriptionId) || 0;
    }
    decrementRef(subscriptionId) {
        const refCount = -1 + this.getRefCount(subscriptionId);
        this.refCountMap.set(subscriptionId, refCount);
        return refCount;
    }
    getSubscriptionFor(params) {
        return _.first(this.getSubscriptions().filter(subscription => subscription.transport === params.transport &&
            subscription.subjectFilter === params.subjectFilter));
    }
    getSubscriptionIdsFor(params) {
        return this.toSubscriptionIds(this.getSubscriptions()
            .filter(subscription => subscription.transport === params.transport));
    }
    toSubscriptionIds(subscriptions) {
        return subscriptions.map(subscription => subscription.subscriptionId);
    }
    getSubscriptionIds() {
        return this.toSubscriptionIds(this.getSubscriptions());
    }
    /**
     * Returns an observable for publications for the subscription.
     *
     * @param {Subscription | string} params.subscription subscription to match.
     * @param {T} params.resource resource to match.
     */
    asObservable(params) {
        const subscriptionId = !params.subscription ? '' : typeof params.subscription === 'string' ?
            params.subscription : params.subscription.subscriptionId;
        const resourceRegex = new RegExp(`/${params.resource}/?[^/]+$`);
        return this.notify.asMessageObservable()
            .filter(publication => publication.topic === this.topic)
            .filter(publication => subscriptionId ? publication.subscription.subscriptionId === subscriptionId :
            params.resource ? resourceRegex.exec(publication.subject) != null : false);
    }
}
exports.TopicFocusedNotify = TopicFocusedNotify;
